import { CourseDetails } from '../types/courseDetails'
import { 
    SelectedUser,
    UserDatum
} from '../types/userDetails'
import { 
    calculateQuizPoints,
    getPrettyDate,
    removeHtmlTags,
    sortDetails,
    sortQuestionCategories
} from '../utils'

const CourseLessonData = ( courseDetails: CourseDetails, selectedUser: SelectedUser ) => {

    const lessonDetails = courseDetails.course_details.filter(details => details.post_type === 'sfwd-lessons')

    // remove the assignments on the lessons
    const assignmentLessonIds = new Set(courseDetails.lessons_meta
        .filter(details => details.meta_value['sfwd-lessons_lesson_assignment_upload'] === 'on')
        .map(details => details.lesson_id)
    )
    
    const lessonIds = courseDetails.post_types['sfwd-lessons']
    const updatedLessonDetails = sortDetails(
                                    lessonDetails.filter(lesson => !assignmentLessonIds.has(lesson.id)),
                                    lessonIds )

    const lessons = selectedUser?.steps?.lessons ?? {}

    return updatedLessonDetails.map((detail: any, index: number)=>{
        const lessonId = Number(detail.id)
        const lessonStatus = lessons[Number(lessonId)] ? 'Completed' : 'Not Started'
        return {
            lessonId: detail.id,
            lessonName: detail.title,
            lessonOrder: index,
            lessonStatus: lessonStatus,
        }
    })
}


const CourseTopicData = ( courseDetails:CourseDetails, selectedUser: SelectedUser ) => {
    const topicIds = courseDetails.post_types['sfwd-topic']
    const topicDetails = sortDetails( 
                            courseDetails.course_details.filter(detail => detail.post_type === 'sfwd-topic'),
                            topicIds )

    const lessonDetails = courseDetails.course_details.filter(detail => detail.post_type === 'sfwd-lessons')
    const courseSteps = courseDetails.course_steps.steps.h['sfwd-lessons']
    
    const topicsSteps = selectedUser?.steps?.topics ?? {}
    
    return topicDetails.map((topic: any, index: number) => {
        const topicId = topic.id
        const lessonId = Object.keys(courseSteps).find(stepId => courseSteps[stepId]['sfwd-topic'][topicId]) 
        const lessonDetail = lessonDetails.find(lesson => lesson.id === lessonId)
      
        const topicStatus = topicsSteps.length ? 
            topicsSteps[Number(lessonId)][topic.id] 
            ? 'Completed' 
            : 'Not Started'
            : 'Not Started'

        return {
            topicId: topic.id,
            topicOrder: index,
            topicName: topic.title,
            topicStatus: topicStatus,
            topicAssociatedLesson: lessonDetail ? lessonDetail.title : ''
        }
    })
}

const QuizData = ( courseDetails:CourseDetails, userDatum: UserDatum ) => {
    const courseId = courseDetails?.course_steps.course_id

    if ( !courseId ) return []
    
    const quizPerCourse = Object.values(userDatum.quizzes.quiz).filter(val => val.course == courseId)
    
    return quizPerCourse.map(( quiz, index ) => {
        
        return {
            quizId: quiz?.quiz,
            quizProId: quiz?.pro_quizid,
            quizName: quiz?.quiz_title,
            quizScore: quiz?.percentage,
            dateCompleted: getPrettyDate( quiz?.completed ),
            statisticRefId: quiz?.statistic_ref_id,
            quizCertificateLink: quiz?.quiz_certificate_link
        }
    })
}

/**
 * This is where the questions and answer will end up
 * @param CourseDetails 
 * @param userDatum 
 */
const QuizDetails = ( selectedQuiz, userDatum: UserDatum ) => {

    const quizId = selectedQuiz?.quizProId
    const refId = selectedQuiz?.statisticRefId

    const questions = Object.values(userDatum.quizzes.questions).filter(val => Number( val.quiz_id ) == quizId )
    const answerData = Object.values(userDatum.quizzes.answers).filter(val => Number( val.statistic_ref_id ) == refId )

    /**
     * Add subtotal;
     * TODO: group the questions per category and then map.
     */

    let currentCategory = 0

    const sorted = sortQuestionCategories( questions )
    const result = []

    sorted.forEach((question, index) => {
        const answer = Object.values( answerData ).find( val => val.question_id == question.id ) || {}

        const correctCount = answer.correct_count ?? 0
        const incorrectCount = answer.incorrect_count ?? 0
        const hintCount = answer.hint_count ?? 0
        const questionTime = answer.question_time ?? 0
        const points = Number(question.points ?? 0)

        const questionCategory = Number( question.category_id )

        if ( currentCategory === 0 ) currentCategory = questionCategory

        const quizData = {
            quizId: question.quiz_id ?? "0",
            categoryId: question.categoryId ?? "0",
            categoryTitle: question.title ?? "",
            quizQuestion: removeHtmlTags(question.question) ?? "",
            quizChoices: question.answer_data ?? {},
            quizPoints: points,
            quizCorrect: correctCount,
            quizIncorrect: incorrectCount,
            quizHintsUsed: hintCount,
            quizPointsScored: answer.points ?? 0,
            quizTime: questionTime,
            quizUserAnswer: answer,
            answerType: question.answer_type ?? ""
        }

        result.push( quizData )

        const isLastQuestion =  ( sorted.length - 1 ) === index 
            
        const nextQuestionCategory = Number( sorted[ isLastQuestion ? index : index + 1]['category_id'] )

        if ( questionCategory !== nextQuestionCategory || isLastQuestion ) {

            const calculateTotal = calculateQuizPoints( answerData, questions, questionCategory, 'sub-total' )

            // Add subtotal row
            result.push({
                quizId: "",
                categoryId: "",
                categoryTitle: `SUBTOTAL: `,
                quizQuestion:  `SUBTOTAL: `,
                quizChoices: {},
                quizPoints: calculateTotal.points ?? 0,
                quizCorrect: calculateTotal.correct ?? 0,
                quizIncorrect: calculateTotal.incorrect ?? 0, 
                quizHintsUsed: calculateTotal ?? 0,
                quizPointsScored: calculateTotal.pointsScored ?? 0,
                quizTime: "",
                quizUserAnswer: answer,
                answerType: question.answer_type ?? ""
            })

            currentCategory = questionCategory
        }

        if( isLastQuestion ){
            //Grand Total
            const calculateTotal = calculateQuizPoints( answerData, questions, 0, 'total' )

            result.push({
                quizId: "",
                categoryId: "",
                categoryTitle: `TOTAL: `,
                quizQuestion: `TOTAL: `,
                quizChoices: {},
                quizPoints: calculateTotal?.points ?? 0,
                quizCorrect: calculateTotal?.correct ?? 0,
                quizIncorrect: calculateTotal?.incorrect ?? 0, 
                quizHintsUsed: calculateTotal?.hints ?? 0,
                quizPointsScored: calculateTotal.pointsScored ?? 0,
                quizTime: "",
                quizUserAnswer: answer,
                answerType: question.answer_type ?? ""
            })
        }
    })
   
    return result
}

const AssignmentData = ( courseDetails:CourseDetails, userDatum: UserDatum ) => {
    const courseId = courseDetails?.course_steps.course_id

    if ( !courseId ) return []

    const assignmentPerCourse = Object.values(userDatum.assignments).filter(val => val.course_id == courseId)

    return assignmentPerCourse.map((assignment, index) => {
        const hasPoints = assignment?.hasOwnProperty('points')
        let points = '-'

        if( hasPoints ) {
            const lessonId = assignment?.lesson_id
            const lessonMeta = courseDetails.lessons_meta.find(lesson => lesson.lesson_id === lessonId)
            const maxAmount = lessonMeta?.meta_value['sfwd-lessons_lesson_assignment_points_amount'] ?? 0
            const assignmentPoint = `${assignment.points} / ${maxAmount}`
            points = assignmentPoint
        }

        return {
            lessonId: assignment?.lesson_id,
            assignmentName: assignment?.lesson_title,
            assignmentApproval: assignment?.approval_status === '1' ? 'Approved' : 'Not Approved',
            assignmentPoints: points,
            assignmentSubmittedDate: assignment?.post_date,
            assignmentDownloadLink: assignment?.file_link
        }
    })
}

export const GetCourseData = ( courseDetails, selectedUser, type, userDatum ) => {
    switch( type ){
        case 'lessons':
            return CourseLessonData( courseDetails, selectedUser )

        case 'topics':
            return CourseTopicData( courseDetails, selectedUser )

        case 'quizzes':
            return QuizData( courseDetails, userDatum )

        case 'assignments':
            return AssignmentData( courseDetails, userDatum )
        
        case 'quiz-details':
            return QuizDetails( courseDetails, userDatum )
    }
}
